import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { FieldIssue } from '../../types';
import type { Campaign, ValidationFeedback, AssetOrderToken } from '@/api/campaigns';
import {
  CharCount,
  FieldLabel,
  InlineIssue,
  Field,
  inputClass,
  issueBorder,
  mergeIssues,
} from './shared';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';
import { seedSignature } from './seedSignature';
import { revokeCachedBlobUrls } from './revokeCachedBlobUrls';
import {
  SlotThumb,
  FEED_RATIOS,
  EMPTY_SLOT,
  emptySlots,
  isSlotFilled,
  type CardSlot,
  type FeedRatio,
} from './feedCardSlots';

// ─── Constants ────────────────────────────────────────────────────────────────

const INTRO_LIMIT = 255;
const HEADLINE_LIMIT = 45;
const MIN_CARDS = 2;
const MAX_CARDS = 10;

// ─── Types ────────────────────────────────────────────────────────────────────

// Same shared slot model as the Meta carousel: a card owns a 1:1 and a 4:5 crop
// which share its headline and destination URL. Only the text fields differ.
interface Card {
  id: number;
  slots: Record<FeedRatio, CardSlot>;
  headline: string;
  destinationUrl: string;
}

let nextId = 1;

function makeNewCard(headline = '', destinationUrl = ''): Card {
  return { id: nextId++, slots: emptySlots(), headline, destinationUrl };
}

/**
 * LinkedIn carousel cards are static images only — the format has no video slot,
 * and the card thumbnail renders through a plain <img>, which shows a broken
 * image icon if handed a video blob.
 *
 * The file inputs set accept="image/*", but that is only a picker hint: it does
 * not apply to drag-and-drop or to Gear Exchange imports. So every path that can
 * introduce a File runs through here.
 */
const ACCEPTED_FILE_TYPES = 'image/*';

function rejectNonImages(files: File[]): File[] {
  const images = files.filter((f) => f.type.startsWith('image/'));
  const rejected = files.length - images.length;
  if (rejected > 0) {
    toast.error(
      rejected === 1
        ? 'LinkedIn carousel cards support images only — that file was not added.'
        : `LinkedIn carousel cards support images only — ${rejected} files were not added.`,
      { duration: 5000 },
    );
  }
  return images;
}

// ─── Card row sub-component ───────────────────────────────────────────────────

function CardRow({
  card,
  index,
  total,
  isActive,
  activeRatio,
  headlineIssue,
  cardImageIssue,
  destinationUrlIssue,
  showErrors,
  showWarnings,
  feedback,
  feedbackPending,
  onFeedback,
  onSelect,
  onSlotFileChange,
  onHeadlineChange,
  onDestinationUrlChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  card: Card;
  index: number;
  total: number;
  isActive: boolean;
  /** Which crop the mockup is currently showing, so the row can highlight it. */
  activeRatio: FeedRatio;
  headlineIssue?: FieldIssue;
  /** Raised when the card has text but no image in either crop. */
  cardImageIssue?: FieldIssue;
  destinationUrlIssue?: FieldIssue;
  showErrors: boolean;
  showWarnings: boolean;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onSelect: (ratio: FeedRatio) => void;
  onSlotFileChange: (ratio: FeedRatio, f: File | null) => void;
  onHeadlineChange: (v: string) => void;
  onDestinationUrlChange: (v: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      className={`relative group/card flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
        isActive ? 'border-slate-200 bg-slate-50' : 'border-slate-100 hover:border-slate-200'
      }`}
      onClick={() => onSelect(activeRatio)}
    >
      {/* ── Delete — top-right, hover only ── */}
      <div
        className="absolute top-2 right-2 opacity-0 group-hover/card:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <button
                type="button"
                onClick={onRemove}
                disabled={total <= MIN_CARDS}
                className="w-6 h-6 rounded flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {total <= MIN_CARDS
              ? `Cannot delete — minimum ${MIN_CARDS} cards required`
              : 'Remove card'}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* ── Thumbnails: the card's two crops ── */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          {FEED_RATIOS.map((r) => (
            <SlotThumb
              key={r}
              slot={card.slots[r]}
              ratio={r}
              index={index}
              isActive={isActive && activeRatio === r}
              accept={ACCEPTED_FILE_TYPES}
              // Cards render through <img>; a video blob would show a broken icon.
              allowVideo={false}
              filterFiles={rejectNonImages}
              onFileChange={(f) => onSlotFileChange(r, f)}
              onSelect={() => onSelect(r)}
            />
          ))}
        </div>
        <InlineIssue
          issue={cardImageIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onFeedback}
        />
      </div>

      {/* ── Text fields ── */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <span
          className={`text-[10px] font-bold tracking-wider ${isActive ? 'text-slate-600' : 'text-slate-400'}`}
        >
          CARD {index + 1}
        </span>
        <div className="relative">
          <input
            type="text"
            value={card.headline}
            onChange={(e) => onHeadlineChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder="Card headline..."
            className={`${inputClass} text-xs py-1.5 pr-12 ${issueBorder(headlineIssue, showErrors, showWarnings)}`}
          />
          <span
            className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tabular-nums pointer-events-none ${
              card.headline.length > HEADLINE_LIMIT
                ? 'text-amber-600 font-medium'
                : 'text-slate-400'
            }`}
          >
            {card.headline.length}/{HEADLINE_LIMIT}
          </span>
        </div>
        <InlineIssue
          issue={headlineIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onFeedback}
        />
        <input
          type="url"
          value={card.destinationUrl}
          onChange={(e) => onDestinationUrlChange(e.target.value)}
          placeholder="Destination URL"
          onClick={(e) => e.stopPropagation()}
          className={`${inputClass} text-xs py-1.5 ${issueBorder(destinationUrlIssue, showErrors, showWarnings)}`}
        />
        <InlineIssue
          issue={destinationUrlIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onFeedback}
        />
      </div>

      {/* ── Reorder arrows ── */}
      <div
        className="flex flex-col gap-1 flex-shrink-0 self-center"
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <button
                type="button"
                onClick={onMoveUp}
                disabled={index === 0}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">
            {index === 0 ? 'Cannot move up — already first in order' : 'Move up'}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={index === total - 1}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-200 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">
            {index === total - 1 ? 'Cannot move down — already last in order' : 'Move down'}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/** Per-crop upload payload. Mirrors MetaCarouselFeedUploads. */
export type LinkedInCarouselUploads = Record<
  FeedRatio,
  { newFiles: File[]; order: AssetOrderToken[] }
>;

export type LinkedInCarouselInitialItem = {
  imageUrl?: string;
  existingAssetId?: string;
  /** The card's 4:5 crop, if one was uploaded. Shares this card's text. */
  imageUrl4x5?: string;
  existingAssetId4x5?: string;
  headline: string;
  destinationUrl: string;
};

export interface LinkedInCarouselDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  caption: string;
  onCaptionChange: (v: string) => void;
  legal: string;
  onLegalChange: (v: string) => void;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  activeCardIndex: number;
  /** Which crop the mockup is showing. Both crops share one LinkedIn mockup. */
  activeRatio?: FeedRatio;
  onSelectCard: (index: number, ratio: FeedRatio) => void;
  /** For mockup display: called whenever cards change */
  onItemsChange: (items: { imageUrl?: string; imageUrl4x5?: string; headline: string }[]) => void;
  /**
   * For save. Uploads are per crop — each ratio is a separate submit with its own
   * ordering — while deletions, count and card text describe the shared card list.
   */
  onCardsChange: (
    uploads: LinkedInCarouselUploads,
    deletedIds: string[],
    totalCount: number,
    cardData: { headline: string; destinationUrl: string }[],
  ) => void;
  /** Existing cards from DB — resolved image URLs, per-card headline/URL */
  initialItems?: LinkedInCarouselInitialItem[];
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function LinkedInCarouselDetails({
  campaignId,
  fordshareIds,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  caption,
  onCaptionChange,
  legal,
  onLegalChange,
  feedback,
  feedbackPending,
  onAIFeedback,
  activeCardIndex,
  activeRatio = '1x1',
  onSelectCard,
  onItemsChange,
  onCardsChange,
  initialItems,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: LinkedInCarouselDetailsProps) {
  const [cards, setCards] = useState<Card[]>([makeNewCard(), makeNewCard()]);
  // Identity of the initial placeholder array, used to detect the pre-seed render.
  const initialCardsRef = useRef(cards);

  // Accumulates existingAssetIds deleted this session (for server-side deletion on save)
  const deletedIdsRef = useRef<string[]>([]);
  // Signature of the initialItems this component last seeded from. Re-seeding is
  // driven by WHICH assets arrived rather than a once-only flag, so the component
  // no longer depends on the parent remounting it to show fresh data.
  // null means "never seeded". See seedSignature.ts.
  const seededSigRef = useRef<string | null>(null);
  // Ref for initialItems so notifyParent can check seeding status without
  // depending on prop identity.
  const initialItemsRef = useRef(initialItems);
  initialItemsRef.current = initialItems;
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const bulkInputRef = useRef<HTMLInputElement>(null);
  // Keyed by `${cardId}:${ratio}` — a card now has two independent previews.
  const blobUrlCacheRef = useRef<Map<string, string>>(new Map());

  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);
  const [gxInjectedFiles, setGxInjectedFiles] = useState<File[]>();
  const [gxInjected4x5, setGxInjected4x5] = useState<File[]>();

  // GX zones are per crop, so they need per-crop occupancy: a card holding only
  // a 1:1 leaves its 4:5 free. (A card-level "has any crop" count would block
  // the 4:5 zone on cards whose 4:5 slot is still empty.)
  const cardsWith1x1 = cards.filter((c) => isSlotFilled(c.slots['1x1'])).length;
  const cardsWith4x5 = cards.filter((c) => isSlotFilled(c.slots['4x5'])).length;

  // Card-count issues now come back under `ratio_items.feed` (which counts distinct
  // card positions, so a card with both crops counts once) plus the per-crop
  // coverage warnings. `asset_files` is kept so issues saved before the split still
  // surface on this section.
  const cardIssue = mergeIssues(
    ['ratio_items.feed', 'ratio_items.1x1', 'ratio_items.4x5', 'asset_files'],
    fieldIssues,
  );

  // Seed (and re-seed) cards from the DB whenever the incoming asset identities change
  useEffect(() => {
    const sig = seedSignature(initialItems);
    if (!sig || sig === seededSigRef.current) return;
    seededSigRef.current = sig;
    // A re-seed adopts server state wholesale, so pending deletions from the
    // previous seed no longer apply — they are already reflected in initialItems.
    deletedIdsRef.current = [];
    // Previous local previews are dropped wholesale by the re-seed below; their
    // cached blob URLs are no longer reachable for per-card cleanup.
    revokeCachedBlobUrls(blobUrlCacheRef.current);
    setCards(
      initialItems!.map((item) => ({
        id: nextId++,
        slots: {
          '1x1':
            item.imageUrl && item.existingAssetId
              ? {
                  kind: 'existing' as const,
                  existingAssetId: item.existingAssetId,
                  previewUrl: item.imageUrl,
                }
              : EMPTY_SLOT,
          '4x5':
            item.imageUrl4x5 && item.existingAssetId4x5
              ? {
                  kind: 'existing' as const,
                  existingAssetId: item.existingAssetId4x5,
                  previewUrl: item.imageUrl4x5,
                }
              : EMPTY_SLOT,
        },
        headline: item.headline,
        destinationUrl: item.destinationUrl,
      })),
    );
  }, [initialItems]);

  // Notify parent whenever cards change
  const notifyParent = useCallback(() => {
    const previewOf = (card: Card, r: FeedRatio): string | undefined => {
      const slot = card.slots[r];
      if (slot.kind === 'existing') return slot.previewUrl;
      if (slot.kind === 'empty') return undefined;
      const key = `${card.id}:${r}`;
      if (!blobUrlCacheRef.current.has(key)) {
        blobUrlCacheRef.current.set(key, URL.createObjectURL(slot.file));
      }
      return blobUrlCacheRef.current.get(key);
    };
    onItemsChange(
      cards.map((c) => ({
        imageUrl: previewOf(c, '1x1'),
        imageUrl4x5: previewOf(c, '4x5'),
        headline: c.headline,
      })),
    );
    // Don't push the placeholder cards up to the parent while we're still waiting
    // for the DB cards to seed, or they'd clobber the parent's card data and
    // ordering with two blank entries (the content then only appears correctly
    // after a manual refresh).
    //
    // We compare against the *initial* cards array by identity rather than
    // checking the seed signature: React runs the seeding effect before this
    // notify effect on mount and records the signature synchronously, but the
    // `setCards` it schedules has not been applied yet — so the component looks
    // seeded while `cards` is still the placeholder array. Identity is exact:
    // any seed or user edit replaces the array, so this only skips the genuine
    // pre-seed render.
    if (initialItemsRef.current?.length && cards === initialCardsRef.current) return;
    // One entry per card, in card order, per ratio — empties included. The index in
    // this array IS the card index, which is what lets a card's two crops find each
    // other again on reload. See the same construction in MetaCarouselDetails.
    const uploads = {} as LinkedInCarouselUploads;
    for (const r of FEED_RATIOS) {
      const newFiles: File[] = [];
      const order: AssetOrderToken[] = [];
      for (const c of cards) {
        const slot = c.slots[r];
        if (slot.kind === 'existing') order.push({ existingAssetId: slot.existingAssetId });
        else if (slot.kind === 'new') {
          order.push({ newFileIndex: newFiles.length });
          newFiles.push(slot.file);
        } else {
          order.push({ empty: true });
        }
      }
      uploads[r] = { newFiles, order };
    }
    onCardsChange(
      uploads,
      deletedIdsRef.current,
      cards.length,
      cards.map((c) => ({ headline: c.headline, destinationUrl: c.destinationUrl })),
    );
  }, [cards, onItemsChange, onCardsChange]);

  useEffect(() => {
    notifyParent();
  }, [notifyParent]);

  function updateCard(id: number, patch: Partial<Pick<Card, 'headline' | 'destinationUrl'>>) {
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }

  // Replace (or clear) one crop of one card. The other crop is untouched — that
  // independence is the whole point of the per-slot model.
  function handleSlotFileChange(card: Card, r: FeedRatio, f: File | null) {
    const key = `${card.id}:${r}`;
    const cached = blobUrlCacheRef.current.get(key);
    if (cached) {
      URL.revokeObjectURL(cached);
      blobUrlCacheRef.current.delete(key);
    }
    const slot = card.slots[r];
    // Overwriting or clearing a saved crop means that row must be deleted server-side.
    if (slot.kind === 'existing' && !deletedIdsRef.current.includes(slot.existingAssetId)) {
      deletedIdsRef.current = [...deletedIdsRef.current, slot.existingAssetId];
    }
    const next: CardSlot = f ? { kind: 'new', file: f } : EMPTY_SLOT;
    setCards((prev) =>
      prev.map((c) => (c.id === card.id ? { ...c, slots: { ...c.slots, [r]: next } } : c)),
    );
  }

  function removeCard(card: Card) {
    if (cards.length <= MIN_CARDS) return;
    // Deleting a card takes both of its crops with it.
    for (const r of FEED_RATIOS) {
      const slot = card.slots[r];
      if (slot.kind === 'existing' && !deletedIdsRef.current.includes(slot.existingAssetId)) {
        deletedIdsRef.current = [...deletedIdsRef.current, slot.existingAssetId];
      }
      const key = `${card.id}:${r}`;
      const cached = blobUrlCacheRef.current.get(key);
      if (cached) {
        URL.revokeObjectURL(cached);
        blobUrlCacheRef.current.delete(key);
      }
    }
    setCards((prev) => prev.filter((c) => c.id !== card.id));
  }

  function moveCard(index: number, direction: -1 | 1) {
    const next = [...cards];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setCards(next);
  }

  function addBulkFiles(fileList: FileList | File[]) {
    const files = rejectNonImages(Array.from(fileList));
    if (files.length === 0) return;
    setCards((prev) => {
      const next = [...prev];
      let fileIdx = 0;
      // Bulk drop/pick fills the 1:1 crop only. A 4:5 is an alternative rendering
      // of a card, so which dropped file is the 4:5 of which card is not something
      // we can infer — those are picked per slot.
      for (let i = 0; i < next.length && fileIdx < files.length; i++) {
        if (!isSlotFilled(next[i].slots['1x1'])) {
          next[i] = {
            ...next[i],
            slots: { ...next[i].slots, '1x1': { kind: 'new', file: files[fileIdx++] } },
          };
        }
      }
      while (fileIdx < files.length && next.length < MAX_CARDS) {
        const card = makeNewCard();
        card.slots = { ...card.slots, '1x1': { kind: 'new', file: files[fileIdx++] } };
        next.push(card);
      }
      return next;
    });
  }

  useEffect(() => {
    if (gxInjectedFiles && gxInjectedFiles.length > 0) addBulkFiles(gxInjectedFiles);
  }, [gxInjectedFiles]); // addBulkFiles is stable

  /**
   * Fills the 4:5 crop, for GX imports only.
   *
   * Drag/drop can't say which dropped file is the 4:5 of which card, so it fills
   * 1:1 only. Gear Exchange does know: the modal detects a true 4:5 and routes it
   * here. Empty 4:5 slots on existing cards go first, so a mixed import lands
   * both crops on the same card; leftovers open new cards.
   */
  function addBulkFiles4x5(fileList: FileList | File[]) {
    const files = rejectNonImages(Array.from(fileList));
    if (files.length === 0) return;
    setCards((prev) => {
      const next = [...prev];
      let fileIdx = 0;
      for (let i = 0; i < next.length && fileIdx < files.length; i++) {
        if (!isSlotFilled(next[i].slots['4x5'])) {
          next[i] = {
            ...next[i],
            slots: { ...next[i].slots, '4x5': { kind: 'new', file: files[fileIdx++] } },
          };
        }
      }
      while (fileIdx < files.length && next.length < MAX_CARDS) {
        const card = makeNewCard();
        card.slots = { ...card.slots, '4x5': { kind: 'new', file: files[fileIdx++] } };
        next.push(card);
      }
      return next;
    });
  }

  useEffect(() => {
    if (gxInjected4x5 && gxInjected4x5.length > 0) addBulkFiles4x5(gxInjected4x5);
  }, [gxInjected4x5]); // addBulkFiles4x5 is stable

  function onZoneDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDraggingFile(true);
  }
  function onZoneDragLeave(e: React.DragEvent) {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingFile(false);
  }
  function onZoneDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDraggingFile(false);
    addBulkFiles(e.dataTransfer.files);
  }

  const destinationUrlIssue = fieldIssues['destination_url'];

  return (
    <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
      {/* ── About ───────────────────────────────────────────────────── */}
      <div className="border-l-2 border-[#0014cc]/30 pl-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
          About this format
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          A <span className="font-medium text-slate-700">horizontally scrollable carousel</span> of
          2–10 cards in the LinkedIn feed. Each card has its own 1:1 or 4:5 image, headline, and
          destination URL. A single CTA button appears on each card.{' '}
          <a
            href="https://business.linkedin.com/advertise/ads/sponsored-content/carousel-ads/specs"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
          >
            LinkedIn Carousel specs ↗
          </a>
        </p>
      </div>

      {/* ── Gear Exchange Import ──────────────────────────────────── */}
      {
        <>
          <GearExchangeImportButton
            totalAssets={gxData?.total_assets ?? null}
            loading={gxLoading}
            error={gxError}
            fordshareIds={fordshareIds ?? undefined}
            onClick={() => setShowGxModal(true)}
          />

          {showGxModal && gxData && campaign && campaignId && (
            <GearExchangeModal
              data={gxData}
              campaign={campaign}
              campaignId={campaignId}
              formatLabel="LinkedIn Carousel"
              zoneLimits={[
                { ratio: '1x1', limit: 10, existing: cardsWith1x1 },
                { ratio: '4x5', limit: 10, existing: cardsWith4x5 },
              ]}
              onClose={() => setShowGxModal(false)}
              usedInVariants={usedInVariants}
              onImport={(files: GXImportedFile[]) => {
                const f1x1 = files.filter((f) => f.ratio === '1x1').map((f) => f.file);
                const f4x5 = files.filter((f) => f.ratio === '4x5').map((f) => f.file);
                if (f1x1.length > 0) setGxInjectedFiles(f1x1);
                if (f4x5.length > 0) setGxInjected4x5(f4x5);
              }}
            />
          )}
        </>
      }

      {/* ── Introductory Text ───────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Primary Text"
          right={<CharCount value={caption} limit={INTRO_LIMIT} />}
          tooltip="Post body text shown above the carousel. Recommended 255 chars."
        />
        <textarea
          value={caption}
          onChange={(e) => onCaptionChange(e.target.value)}
          placeholder="Write something that will make your audience stop scrolling..."
          rows={4}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['primary_text'], showErrors, showWarnings)}`}
        />
        <InlineIssue
          issue={fieldIssues['primary_text']}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Cards ───────────────────────────────────────────────────── */}
      <div>
        <input
          ref={bulkInputRef}
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) {
              addBulkFiles(e.target.files);
              e.target.value = '';
            }
          }}
        />

        <div onDragOver={onZoneDragOver} onDragLeave={onZoneDragLeave} onDrop={onZoneDrop}>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Cards
              <span className="normal-case font-normal tracking-normal ml-1">
                ({cards.length} / {MAX_CARDS})
              </span>
            </p>
            <p className="text-[11px] text-slate-400">Click a card to preview it</p>
          </div>
          <div className="border-b border-slate-200 mb-3" />

          <div
            className={`space-y-2 rounded-xl transition-all ${isDraggingFile ? 'ring-2 ring-[#0014cc]/30 bg-[#0014cc]/[0.02]' : ''}`}
          >
            {cards.map((card, i) => (
              <CardRow
                key={card.id}
                card={card}
                index={i}
                total={cards.length}
                isActive={i === activeCardIndex}
                activeRatio={activeRatio}
                headlineIssue={fieldIssues[`headline.${i}`] ?? fieldIssues['headline']}
                cardImageIssue={fieldIssues[`card_image.${i}`]}
                destinationUrlIssue={destinationUrlIssue}
                showErrors={showErrors}
                showWarnings={showWarnings}
                feedback={feedback}
                feedbackPending={feedbackPending}
                onFeedback={onAIFeedback}
                onSelect={(r) => onSelectCard(i, r)}
                onSlotFileChange={(r, f) => handleSlotFileChange(card, r, f)}
                onHeadlineChange={(v) => updateCard(card.id, { headline: v })}
                onDestinationUrlChange={(v) => updateCard(card.id, { destinationUrl: v })}
                onMoveUp={() => moveCard(i, -1)}
                onMoveDown={() => moveCard(i, 1)}
                onRemove={() => removeCard(card)}
              />
            ))}
          </div>

          {/* Add card */}
          {cards.length < MAX_CARDS && (
            <button
              type="button"
              onClick={() => bulkInputRef.current?.click()}
              className={`mt-2 w-full py-2.5 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 transition-all ${
                isDraggingFile
                  ? 'border-[#0014cc]/50 bg-[#0014cc]/5 text-[#0014cc]'
                  : cardIssue?.errors.length && showErrors
                    ? 'border-red-400 bg-red-50/20 text-red-400 hover:border-red-500 hover:text-red-500'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-400 hover:text-slate-500'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span className="text-xs font-medium">
                {isDraggingFile ? 'Drop files to add cards' : 'Add card or drop files here'}
              </span>
            </button>
          )}
          <InlineIssue
            issue={cardIssue}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onAIFeedback}
          />
        </div>
      </div>

      {/* ── Legal ───────────────────────────────────────────────────── */}
      <Field
        label="Legal"
        tooltip="Required in some markets (e.g. France) for regulated categories. Internal only — not displayed in the mockup."
        fieldKey="legal"
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
      >
        <textarea
          value={legal}
          onChange={(e) => onLegalChange(e.target.value)}
          placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
          rows={5}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
        />
      </Field>
    </div>
  );
}
